import { ConfirmDisableProjectRoleModal } from "@/components/ProjectRoleEditor/components/ConfirmDisableProjectRoleModal";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { IProjectRole } from "@/interfaces";
import { useUpdateProjectRoleActiveStateMutation } from "@/services/projectRole";
import { SpaceWithFullWidth } from "@/sharedStyled";
import { Card, Form, Input, Row, Switch } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  role: IProjectRole;
  originalRole: IProjectRole;
  handleUnsavedChanges(hasUnsavedChanges: boolean): void;
}

export const ProjectRoleNameEditor = ({
  role,
  originalRole,
  handleUnsavedChanges,
}: IProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const [milestoneEditor, setMilestoneEditor] = useState<boolean>();
  const [markRoleInactiveModalOpen, setMarkRoleInactiveModelOpen] =
    useState<boolean>(false);
  const [updateProjectRoleActiveState] =
    useUpdateProjectRoleActiveStateMutation();
  const { updateProjectRole, setHasErrors } = useProjectRoleContext();

  const toggleIsActive = (isActive: boolean): void => {
    if (isActive) {
      updateProjectRoleActiveState({ id: role.id, isActive: isActive });
      updateProjectRole({ isActive: isActive });
    } else {
      setMarkRoleInactiveModelOpen(true);
    }
  };

  const handleModalClose = (wasDisabled: boolean) => {
    if (wasDisabled) updateProjectRole({ isActive: false });

    setMarkRoleInactiveModelOpen(false);
  };

  useEffect(() => {
    const isMilestoneEditor = role?.isMilestoneEditor;
    setMilestoneEditor((current) =>
      current !== isMilestoneEditor ? isMilestoneEditor : current
    );
    handleUnsavedChanges(
      role?.isMilestoneEditor !== originalRole?.isMilestoneEditor ||
        role?.name !== originalRole?.name
    );
  }, [role, originalRole, handleUnsavedChanges]);

  return (
    <Card
      title={intl.formatMessage({ id: "projectRoles.details" })}
      className="h-full"
      loading={!role}
    >
      {!role ? (
        <Form form={form} component={false} />
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={role}
          onValuesChange={(changedValues): void => {
            updateProjectRole({ ...changedValues });
            setHasErrors(form.getFieldsError().values.length > 0);
          }}
        >
          <Row className="pb-2">
            <SpaceWithFullWidth direction="vertical" size="small">
              <Form.Item
                required
                name="name"
                label={intl.formatMessage({ id: "projectRoles.colName" })}
              >
                <Input data-test-id="template-role-editor-name" />
              </Form.Item>
            </SpaceWithFullWidth>
          </Row>
          <Row className="pb-2">
            <SpaceWithFullWidth direction="vertical" size="small">
              <Form.Item
                name="isMilestoneEditor"
                label={intl.formatMessage({
                  id: "projectRoles.milestoneEditor",
                })}
              >
                <Switch
                  data-test-id="template-role-editor-milestone-editor"
                  checked={milestoneEditor}
                  onChange={(): void => setMilestoneEditor(!milestoneEditor)}
                />
              </Form.Item>
            </SpaceWithFullWidth>
            <SpaceWithFullWidth direction="vertical" size="small">
              <Form.Item label="Is Active">
                <Switch
                  data-test-id="template-role-isactive-toggle"
                  checked={role.isActive}
                  onChange={(checked: boolean): void => toggleIsActive(checked)}
                />
              </Form.Item>
              <ConfirmDisableProjectRoleModal
                projectRoleId={role.id}
                onModalClose={handleModalClose}
                isOpen={markRoleInactiveModalOpen}
              />
            </SpaceWithFullWidth>
          </Row>
        </Form>
      )}
    </Card>
  );
};
